import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Modal, Button, Stack, TextField, Typography } from '@mui/material';
import { cores, AutoComplete, labelCampoEscuro } from '../styles/styleUtils';
import { getGenericData } from './api';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '50vw',
  minHeight: '50vh',
  maxHeight: '90vh',
  '@media screen and (max-width: 600px)': { width: '90vw' },
  bgcolor: 'transparent',
  border: `1px solid ${cores.chumbo}`, borderTop: `4px solid ${cores.laranja}`,
  boxShadow: 24,
  p: 3,
  display: 'flex',
  flexDirection: 'column',
  overflowY: 'auto',
  background: `linear-gradient(180deg, ${cores.painelTopo} 0%, ${cores.painelBase} 100%)`,
  alignItems: 'center',
  justifyContent: 'center',
  gap: 2,
};

const ModalRejeitarProposta = React.forwardRef((props, ref) => {
  const [obs,    setObs]    = useState('');
  const [motivos, setMotivos] = useState([]);

  useEffect(() => {
    const getMotivos = async () => {
      const result = await getGenericData('motivorejeicao', {});
      setMotivos(result);
    };
    getMotivos();
  }, []);

  const motivoSelecionado = props.proposta?.motivoRejeicao;
  const isCpfReprovado    = motivoSelecionado?.nome === 'CPF Reprovado';
  const isCnpjReprovado   = motivoSelecionado?.nome === 'CNPJ Reprovado';

  const cliente = props.proposta?.cliente || {};

  // Campos do cliente já cadastrados — apenas exibição para conferência
  const cpfCliente  = cliente.cpf  || '—';
  const cnpjCliente = cliente.cnpj || '—';
  const dataNasc    = cliente.datanascimento
    ? new Date(cliente.datanascimento).toLocaleDateString('pt-BR')
    : '—';
  const dataFund    = cliente.datafundacao
    ? new Date(cliente.datafundacao).toLocaleDateString('pt-BR')
    : '—';
  const telefone    = cliente.telefone || '—';

  // Confirmação habilitada: CPF/CNPJ Reprovado exige que o cliente tenha os dados cadastrados
  const confirmHabilitado = (() => {
    if (!motivoSelecionado) return false;
    if (isCpfReprovado)  return !!cliente.cpf  && !!cliente.datanascimento && !!cliente.telefone;
    if (isCnpjReprovado) return !!cliente.cnpj && !!cliente.datafundacao   && !!cliente.telefone;
    return true;
  })();

  const handleConfirm = () => props.onConfirm(obs);

  return (
    <Modal open={props.open} onClose={props.onClose}>
      <Box sx={style}>
        <Typography fontWeight="bold" sx={{ color: cores.textoClaro }}>Informe o motivo</Typography>

        <AutoComplete
          disablePortal
          id="motivoRejeicao"
          value={motivoSelecionado || null}
          onChange={(_, value) => {
            props.onAutoCompleteChange('idMotivoRejeicao', 'motivoRejeicao', value);
            setObs('');
          }}
          options={motivos}
          isOptionEqualToValue={(o, v) => o._id === v._id}
          getOptionLabel={(o) => o.nome}
          renderInput={(params) => <TextField {...params} placeholder="Selecione" />}
          sx={{ width: '70%' }}
        />

        {/* Dados do cliente para conferência — CPF Reprovado */}
        {isCpfReprovado && (
          <Box sx={{ width: '70%', backgroundColor: '#fff3e0', border: '1px solid #e65100',
            borderRadius: 1, p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="caption" color="#e65100" fontWeight="bold">
              Dados do cliente cadastrado (conferência):
            </Typography>
            <Typography variant="body2">CPF: <strong>{cpfCliente}</strong></Typography>
            <Typography variant="body2">Data de Nascimento: <strong>{dataNasc}</strong></Typography>
            <Typography variant="body2">Telefone: <strong>{telefone}</strong></Typography>
            {!confirmHabilitado && (
              <Typography variant="caption" color="error">
                ⚠ Para confirmar CPF Reprovado, o cadastro do cliente deve ter CPF, Data de Nascimento e Telefone preenchidos.
              </Typography>
            )}
          </Box>
        )}

        {/* Dados do cliente para conferência — CNPJ Reprovado */}
        {isCnpjReprovado && (
          <Box sx={{ width: '70%', backgroundColor: '#fff3e0', border: '1px solid #e65100',
            borderRadius: 1, p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="caption" color="#e65100" fontWeight="bold">
              Dados do cliente cadastrado (conferência):
            </Typography>
            <Typography variant="body2">CNPJ: <strong>{cnpjCliente}</strong></Typography>
            <Typography variant="body2">Data de Fundação: <strong>{dataFund}</strong></Typography>
            <Typography variant="body2">Telefone: <strong>{telefone}</strong></Typography>
            {!confirmHabilitado && (
              <Typography variant="caption" color="error">
                ⚠ Para confirmar CNPJ Reprovado, o cadastro do cliente deve ter CNPJ, Data de Fundação e Telefone preenchidos.
              </Typography>
            )}
          </Box>
        )}

        <TextField
          label="Observações adicionais" variant="outlined" multiline rows={3}
          value={obs} onChange={(e) => setObs(e.target.value)}
          sx={{ width: '70%', backgroundColor: 'white', ...labelCampoEscuro }}
        />

        <Stack direction="row" justifyContent="space-between" gap={20}>
          <Button variant="outlined" color="error" onClick={props.onClose}>Cancelar</Button>
          <Button variant="contained" color="success"
            disabled={!confirmHabilitado} onClick={handleConfirm}>
            Confirmar
          </Button>
        </Stack>
      </Box>
    </Modal>
  );
});

ModalRejeitarProposta.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
  proposta: PropTypes.object,
  onAutoCompleteChange: PropTypes.func,
};

export default ModalRejeitarProposta;